import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { systemMonitoringService } from '../../../services/systemMonitoringService';

const SystemStatusCard = () => {
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSystemStatus();

    // Subscribe to real-time updates
    const subscription = systemMonitoringService?.subscribeToSystemStatus((updatedStatus) => {
      setSystemStatus(updatedStatus);
    });

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, []);

  const loadSystemStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const status = await systemMonitoringService?.getSystemStatus();
      setSystemStatus(status);
    } catch (err) {
      console.error('Error loading system status:', err);
      setError(err?.message || 'Failed to load system status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'operational': 'text-success',
      'maintenance': 'text-warning',
      'degraded': 'text-warning',
      'error': 'text-error'
    };
    return colorMap?.[status] || 'text-muted-foreground';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'operational': 'CheckCircle',
      'maintenance': 'AlertTriangle',
      'degraded': 'AlertCircle',
      'error': 'XCircle'
    };
    return iconMap?.[status] || 'Circle';
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      'operational': 'Operational',
      'maintenance': 'Maintenance Mode',
      'degraded': 'Degraded Performance',
      'error': 'Error'
    };
    return labelMap?.[status] || 'Unknown';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 text-error mb-2">
          <Icon name="AlertCircle" size={20} />
          <p className="text-sm font-medium">Error loading system status</p>
        </div>
        <p className="text-sm text-muted-foreground">{error}</p>
        <button
          onClick={loadSystemStatus}
          className="mt-4 text-sm text-primary hover:text-primary/80 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!systemStatus) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground">
          System Status
        </h3>
        <div className={`flex items-center gap-2 ${getStatusColor(systemStatus?.status)}`}>
          <Icon name={getStatusIcon(systemStatus?.status)} size={20} />
          <span className="text-sm font-medium">
            {getStatusLabel(systemStatus?.status)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Active Users
            </span>
          </div>
          <p className="text-2xl font-bold text-card-foreground">
            {systemStatus?.activeUsers?.toLocaleString()}
          </p>
        </div>

        <div className="bg-muted/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Database" size={16} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Database Size
            </span>
          </div>
          <p className="text-2xl font-bold text-card-foreground">
            {systemStatus?.databaseSize}
          </p>
        </div>

        <div className="bg-muted/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="HardDrive" size={16} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Storage Used
            </span>
          </div>
          <p className="text-2xl font-bold text-card-foreground">
            {systemStatus?.storageUsed}
          </p>
        </div>

        <div className="bg-muted/30 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={16} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Uptime
            </span>
          </div>
          <p className="text-2xl font-bold text-card-foreground">
            {systemStatus?.uptime}
          </p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Clock" size={14} />
          <span>Last updated: {systemStatus?.lastUpdated?.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })}</span>
        </div>
      </div>
    </div>
  );
};

export default SystemStatusCard;